import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, InputGroup, List, InputNumber } from 'rsuite';
import SearchIcon from '@rsuite/icons/Search';
import ArrowLeftIcon from '@rsuite/icons/ArrowLeft';
import { FaPizzaSlice } from 'react-icons/fa';
import api from '../../../../../../services/api';
import { toastSuccess, toastError } from '../../../../../../toasts';
import * as S from './styles';
import RModal from '../../../../../../components/Rsuite/Modal';

export const LinkBorderModal = ({
  modalOpen,
  toggleModal,
  idEmpresa,
  idGrupo,
  tamanhos = [],
  existingBorders = [],
  onSave,
  selecteds = [],
}) => {
  const [step, setStep] = useState(0);
  const [availableBorders, setAvailableBorders] = useState([]);
  const [filteredBorders, setFilteredBorders] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedBorder, setSelectedBorder] = useState(null);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(false);

  const usedTamanhos = tamanhos.filter((tam) => {
    if (!selecteds || selecteds.length === 0) return false;

    return selecteds.some((x) => {
      const isMatchId =
        String(x.Id) === String(tam.Id) ||
        String(x.IdTamanho) === String(tam.Id);
      const isAtivo =
        'Ativo' in x
          ? x.Ativo === true || x.Ativo === 1 || x.Ativo === '1'
          : true;

      return isMatchId && isAtivo;
    });
  });

  const loadBorders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `/produto/listarBordasPorCompany/${idEmpresa}`,
      );
      const allBorders = response.data || [];
      const unlinkedBorders = allBorders.filter(
        (b) => !existingBorders.some((eb) => eb.descricao === b.Descricao),
      );

      setAvailableBorders(unlinkedBorders);
      setFilteredBorders(unlinkedBorders);
    } catch (error) {
      console.error('Erro ao carregar bordas:', error);
      toastError('Erro ao buscar bordas disponíveis.');
    } finally {
      setLoading(false);
    }
  }, [idEmpresa, existingBorders]);

  useEffect(() => {
    if (modalOpen) {
      setStep(0);
      setSearch('');
      setSelectedBorder(null);
      setPrices({});
      loadBorders();
    }
  }, [modalOpen, loadBorders]);

  useEffect(() => {
    const filtered = availableBorders.filter((item) =>
      item.Descricao.toLowerCase().includes(search.toLowerCase()),
    );
    setFilteredBorders(filtered);
  }, [search, availableBorders]);

  const handleSelectBorder = (border) => {
    setSelectedBorder(border);
    const existingTamanhos = border.tamanhos || border.PrecosTamanhos || [];

    const initialPrices = {};
    tamanhos.forEach((tam) => {
      const existing = existingTamanhos.find(
        (t) =>
          String(t.id || t.idTamanho) === String(tam.Id) ||
          t.descricao === tam.Descricao,
      );

      initialPrices[tam.Id] = {
        preco: existing ? +existing.preco : 0,
        precoDesconto: existing ? +existing.precoDesconto : 0,
      };
    });
    setPrices(initialPrices);
    setStep(1);
  };

  const handlePriceChange = (idTamanho, field, value) => {
    setPrices((prev) => ({
      ...prev,
      [idTamanho]: {
        ...prev[idTamanho],
        [field]: Number(value) || 0,
      },
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const precosTamanhos = tamanhos.map((tam) => ({
        idTamanho: tam.Id,
        preco: Number(prices[tam.Id]?.preco) || 0,
        precoDesconto: Number(prices[tam.Id]?.precoDesconto) || 0,
      }));

      const payload = {
        Grupo: idGrupo,
        PrecosTamanhos: precosTamanhos,
      };

      await api.patch(`produto/alterar/${selectedBorder.Id}`, payload);

      toastSuccess('Borda vinculada com sucesso!');
      if (onSave) onSave();
      toggleModal(false);
    } catch (error) {
      console.error(error);
      toastError('Erro ao vincular borda.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <RModal
      open={modalOpen}
      onClose={() => toggleModal(false)}
      size="md"
      title={
        step === 0 ? 'Vincular Borda Existente' : 'Configurar Preços da Borda'
      }
      bodyStyle={{ overflow: 'hidden', padding: 0 }}
      footer={
        <S.FooterActions>
          <Button onClick={() => toggleModal(false)} appearance="subtle">
            Cancelar
          </Button>
          {step === 1 && (
            <Button
              onClick={handleSubmit}
              appearance="primary"
              loading={loading}
            >
              Concluir Vínculo
            </Button>
          )}
        </S.FooterActions>
      }
    >
      {step === 0 && (
        <S.Container>
          <S.SearchContainer>
            <InputGroup inside style={{ width: '100%' }}>
              <InputGroup.Addon>
                <SearchIcon />
              </InputGroup.Addon>
              <Input
                placeholder="Buscar borda pelo nome..."
                value={search}
                onChange={setSearch}
                autoFocus
              />
            </InputGroup>
          </S.SearchContainer>

          <S.ListContainer>
            {loading ? (
              <S.LoadingText>Carregando bordas...</S.LoadingText>
            ) : filteredBorders.length > 0 ? (
              <List hover>
                {filteredBorders
                  .filter(x => !x.IdProdutoOriginal)
                  .map((item) => (
                  <List.Item
                    key={item.Id}
                    style={{ padding: '4px 0', width: 380 }}
                  >
                    <S.ListItemContent onClick={() => handleSelectBorder(item)}>
                      <S.ItemInfo>
                        <S.ItemLabel>{item.Descricao}</S.ItemLabel>
                        {item.CodigoPDV && (
                          <S.ItemCode>Cód: {item.CodigoPDV}</S.ItemCode>
                        )}
                      </S.ItemInfo>
                      <Button size="sm" appearance="ghost">
                        Selecionar
                      </Button>
                    </S.ListItemContent>
                  </List.Item>
                ))}
              </List>
            ) : (
              <S.EmptyState>
                Nenhuma borda disponível encontrada para vínculo.
              </S.EmptyState>
            )}
          </S.ListContainer>
        </S.Container>
      )}

      {step === 1 && (
        <S.StepContainer>
          <S.SelectedBorderInfo>
            <span>{selectedBorder?.Descricao}</span>
            <Button
              size="sm"
              onClick={() => setStep(0)}
              startIcon={<ArrowLeftIcon />}
              appearance="primary"
              color="blue"
            >
              Trocar Borda
            </Button>
          </S.SelectedBorderInfo>

          <p style={{ marginBottom: 16, fontSize: 14, color: '#8e8e93' }}>
            Defina os valores desta borda para os tamanhos ativos do grupo
            atual:
          </p>

          <S.PricesContainer>
            {usedTamanhos.length === 0 ? (
              <S.EmptyState>
                Nenhum tamanho ativo encontrado neste grupo.
              </S.EmptyState>
            ) : (
              usedTamanhos.map((tam) => (
                <S.PriceRow key={tam.Id}>
                  <div className="label-col">
                    <FaPizzaSlice
                      color="#ccc"
                      size={14}
                      style={{ marginRight: 8 }}
                    />
                    {tam.Descricao}
                  </div>

                  <div className="inputs-wrapper">
                    <div className="input-col">
                      <label>Preço Padrão</label>
                      <InputGroup inside>
                        <InputGroup.Addon>R$</InputGroup.Addon>
                        <InputNumber
                          value={prices[tam.Id]?.preco}
                          onChange={(val) =>
                            handlePriceChange(tam.Id, 'preco', val)
                          }
                          min={0}
                          step={0.5}
                        />
                      </InputGroup>
                    </div>

                    <div className="input-col">
                      <label>Preço Promocional</label>
                      <InputGroup inside>
                        <InputGroup.Addon>R$</InputGroup.Addon>
                        <InputNumber
                          value={prices[tam.Id]?.precoDesconto}
                          onChange={(val) =>
                            handlePriceChange(tam.Id, 'precoDesconto', val)
                          }
                          min={0}
                          step={0.5}
                        />
                      </InputGroup>
                    </div>
                  </div>
                </S.PriceRow>
              ))
            )}
          </S.PricesContainer>
        </S.StepContainer>
      )}
    </RModal>
  );
};
